import { useFactors } from "./state/FactorsContext";
import { actBreakdown } from "./lib/calc";
import Starfield from "./components/Starfield";
import Ticker from "./components/Ticker";
import SidePanel from "./components/SidePanel";
import Opening from "./components/Opening";
import Act from "./components/Act";
import Finale from "./components/Finale";

/**
 * The cinematic descent.
 *
 * A pinned Ticker keeps the live number on screen at all times. Below it the
 * reader scrolls through the Opening (the vast starting number + the controls),
 * then one Act per category — watching the count step down — and finally the
 * Finale, where the number lands and the analogy makes it land emotionally.
 *
 * Everything is data-driven: acts come straight from the categories in
 * factors.json, sliced from the live calculation by `actBreakdown`.
 */
export default function App() {
  const { data, result } = useFactors();

  const categoryIds = data.categories.map((c) => c.id);
  const acts = actBreakdown(result, categoryIds);
  const categoryById = new Map(data.categories.map((c) => [c.id, c]));

  return (
    <div className="relative min-h-screen overflow-x-clip bg-void text-white">
      <Starfield />

      {/* Subtle vignette so content floats above the starfield. */}
      <div
        aria-hidden
        className="pointer-events-none fixed inset-0 -z-0"
        style={{
          background:
            "radial-gradient(ellipse at 50% 0%, rgba(18,23,50,0.55), transparent 60%)",
        }}
      />

      <div className="relative z-10">
        {/* Mobile keeps the number pinned at the top; desktop uses the side panel. */}
        <Ticker />

        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:grid lg:grid-cols-[minmax(300px,360px)_minmax(0,1fr)] lg:gap-12">
          <SidePanel />

          <main className="min-w-0">
            <Opening />

            {acts.map((act, i) => {
              const category = categoryById.get(act.categoryId);
              if (!category) return null;
              return (
                <Act
                  key={act.categoryId}
                  index={i}
                  category={category}
                  factors={act.steps.map((s) => s.factor)}
                  breakdown={act}
                />
              );
            })}

            <Finale />
          </main>
        </div>

        <footer className="mx-auto max-w-2xl px-6 pb-16 text-center text-xs leading-relaxed text-white/50">
          Every biological probability is an extrapolation from a single
          example — our own. The point is not the exact number but how violently
          it swings between hope and silence. Move the sliders; explore the range
          of the possible.
        </footer>
      </div>
    </div>
  );
}
